import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import React, { useContext, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { IncidentContext } from './ReportIncident'
import PersonInvolvedDialog, { getId } from '../PersonInvolvedDialog'
import PersonInvolvedCard from '../PersonInvolvedCard'

const Step3 = () => {
  const { formValues, setFormValues, setTab } = useContext(IncidentContext)
  const [peopleInvolved, setPeopleInvoled] = useState(
    formValues?.peopleInvolvedBoolean ?? '',
  )
  const [people, setPeople] = useState(formValues?.peopleInvolved ?? [])
  // Employees the reporter has chosen to hide the incident from — sent as
  // `hiddenFrom` on submit.
  const [hiddenFrom, setHiddenFrom] = useState(formValues?.hiddenFrom ?? [])
  const [dialog, setDialog] = useState(null) // { index } | null
  const [validationError, setValidationError] = useState('')

  const isHiddenFrom = (person) => {
    const employeeId = getId(person?.employee)
    return !!employeeId && hiddenFrom.some((x) => getId(x) === employeeId)
  }

  const setHidden = (employeeId, hidden) => {
    if (!employeeId) return
    setHiddenFrom((prev) => {
      const without = prev.filter((x) => getId(x) !== employeeId)
      return hidden ? [...without, employeeId] : without
    })
  }

  const savePerson = ({ person, hideFromEmployee }) => {
    setValidationError('')
    setPeople((prev) =>
      dialog?.index === undefined || dialog.index === null
        ? [...prev, person]
        : prev.map((x, i) => (i === dialog.index ? person : x)),
    )
    setHidden(getId(person.employee), hideFromEmployee)
  }

  const removePerson = (index) =>
    setPeople((prev) => prev.filter((x, i) => i !== index))

  const handleNext = (e) => {
    e.preventDefault()
    if (peopleInvolved === 'Yes' && people.length === 0) {
      setValidationError(
        'Add at least one person, or answer "No" if nobody else was involved.',
      )
      return
    }
    const keep = peopleInvolved === 'Yes' ? people : []
    // Only keep hide selections that still correspond to a listed employee.
    const listedEmployees = new Set(
      keep.map((person) => getId(person.employee)).filter(Boolean),
    )
    setFormValues((prev) => ({
      ...prev,
      peopleInvolved: keep,
      peopleInvolvedBoolean: peopleInvolved,
      hiddenFrom: hiddenFrom.filter((x) => listedEmployees.has(getId(x))),
    }))
    setTab((prev) => prev + 1)
    window.scrollTo(0, 0)
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={3}
      component="form"
      onSubmit={handleNext}
    >
      <Typography variant="title2" fontSize="1rem">
        People
      </Typography>
      <FormControl>
        <InputLabel id="personInvolved-label">
          Was any other person involved in the Incident?
        </InputLabel>
        <Select
          labelId="personInvolved-label"
          id="personInvolved"
          value={peopleInvolved}
          label="Was any other person involved in the Incident?"
          onChange={(event) => {
            setPeopleInvoled(event.target.value)
            setValidationError('')
          }}
          required
        >
          <MenuItem value="No">No</MenuItem>
          <MenuItem value="Yes">Yes</MenuItem>
        </Select>
      </FormControl>
      {peopleInvolved === 'Yes' && (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
          >
            <Box>
              <Typography variant="title3" fontSize="1rem">
                People involved
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select an employee from your staff list, or type in details for
                anyone else (patient, member of the public, contractor).
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="info"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ index: null })}
            >
              Add Person
            </Button>
          </Box>
          {people.length === 0 && (
            <Alert
              severity="info"
              icon={<PersonOutlineIcon />}
              sx={{ borderRadius: 2 }}
            >
              No people added yet — use "Add Person" to add someone.
            </Alert>
          )}
          <Grid container spacing={1.5}>
            {people.map((person, index) => (
              <Grid item xs={12} md={6} key={index}>
                <PersonInvolvedCard
                  person={person}
                  index={index}
                  hidden={isHiddenFrom(person)}
                  onEdit={() => setDialog({ index })}
                  onRemove={() => removePerson(index)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {validationError && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {validationError}
        </Alert>
      )}

      <Box display="flex" gap={1} justifyContent="flex-end">
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setTab((prev) => prev - 1)}
        >
          Previous
        </Button>
        <Button variant="contained" color="primary" type="submit">
          Next
        </Button>
      </Box>

      <PersonInvolvedDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        onSave={savePerson}
        person={
          dialog?.index !== null && dialog?.index !== undefined
            ? people[dialog.index]
            : null
        }
        isHidden={
          dialog?.index !== null && dialog?.index !== undefined
            ? isHiddenFrom(people[dialog.index])
            : false
        }
      />
    </Box>
  )
}

export default Step3
